import { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Users, UserCheck, ListChecks, Mail, Plus, FolderPlus } from "lucide-react";

export type MailingListAddOptions = {
  flagOption: "active" | "gatekeeper";
  quantity: "all" | "one";
  destination: "new" | "existing";
};

type OptionCardProps<T extends string> = {
  value: T;
  selected: T;
  onSelect: (value: T) => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
};

function OptionCard<T extends string>({
  value,
  selected,
  onSelect,
  icon,
  title,
  description,
  disabled,
}: OptionCardProps<T>) {
  const isSelected = selected === value;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(value)}
      className={`flex-1 text-left px-3 py-2.5 rounded-lg border transition-colors disabled:opacity-50 ${
        isSelected
          ? "border-primary-600 bg-primary-900/20"
          : "border-dark-border bg-dark-bg hover:bg-dark-border"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={isSelected ? "text-primary-400" : "text-dark-text-muted"}>{icon}</span>
        <span className="text-sm font-medium text-dark-text-primary">{title}</span>
      </div>
      <p className="text-xs text-dark-text-muted mt-1 leading-snug">{description}</p>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-xs font-semibold uppercase tracking-wide text-dark-text-secondary mb-1.5">
      {children}
    </span>
  );
}

// --- Intermediate filter + destination chooser for adding venues to a mailing list ---
export function AddVenuesToMailingListModal({
  isOpen,
  onClose,
  venueCount,
  isResolving = false,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  venueCount: number;
  isResolving?: boolean;
  onSubmit: (opts: MailingListAddOptions) => void;
}) {
  const [flagOption, setFlagOption] = useState<"active" | "gatekeeper">("active");
  const [quantity, setQuantity] = useState<"all" | "one">("all");
  const [destination, setDestination] = useState<"new" | "existing">("new");

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add to Mailing List" size="md">
      <div className="p-4 space-y-4">
        <p className="text-sm text-dark-text-secondary">
          Collect contacts from{" "}
          <span className="font-medium text-dark-text-primary">
            {venueCount} {venueCount === 1 ? "venue" : "venues"}
          </span>{" "}
          and add them to a mailing list. Expired and do-not-contact contacts are always excluded.
        </p>

        {/* Contact flag */}
        <div>
          <SectionLabel>Contacts to include</SectionLabel>
          <div className="flex gap-2">
            <OptionCard
              value="active"
              selected={flagOption}
              onSelect={setFlagOption}
              icon={<Users className="w-4 h-4" />}
              title="Active"
              description="Regular contacts (excludes gatekeepers)."
            />
            <OptionCard
              value="gatekeeper"
              selected={flagOption}
              onSelect={setFlagOption}
              icon={<UserCheck className="w-4 h-4" />}
              title="Gatekeeper"
              description="Only contacts flagged as gatekeepers."
            />
          </div>
        </div>

        {/* Quantity */}
        <div>
          <SectionLabel>How many per venue</SectionLabel>
          <div className="flex gap-2">
            <OptionCard
              value="all"
              selected={quantity}
              onSelect={setQuantity}
              icon={<ListChecks className="w-4 h-4" />}
              title="All matching"
              description="Every matching contact of each venue."
            />
            <OptionCard
              value="one"
              selected={quantity}
              onSelect={setQuantity}
              icon={<Mail className="w-4 h-4" />}
              title="One per venue"
              description="Most recently emailed; first if none."
            />
          </div>
        </div>

        {/* Destination */}
        <div>
          <SectionLabel>Destination</SectionLabel>
          <div className="flex gap-2">
            <OptionCard
              value="new"
              selected={destination}
              onSelect={setDestination}
              icon={<FolderPlus className="w-4 h-4" />}
              title="Create new list"
              description="Start a fresh mailing list."
            />
            <OptionCard
              value="existing"
              selected={destination}
              onSelect={setDestination}
              icon={<Plus className="w-4 h-4" />}
              title="Add to existing"
              description="Append to a list you already have."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" type="button" onClick={onClose} disabled={isResolving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            isLoading={isResolving}
            onClick={() => onSubmit({ flagOption, quantity, destination })}
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}
